import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IGetPixByOrderService } from "@modules/orders/models/IGetPixByOrderService";
import { PixPayload } from "@pages/User/MyOrders/models/IPixPayment";
import {
  getPixAggregates,
  isPixPaid,
  preparePixPayloadsForModal,
  PixAggregates,
} from "@pages/User/MyOrders/utilsOrders/utilsMyOrders";
import { PixModalPaymentStatus } from "../types";

interface UsePixPaymentModalOptions {
  salesOrderNumber: string | null;
  bp?: string | null;
  enabled?: boolean;
  pollingInterval?: number;
}

interface UsePixPaymentModalReturn {
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
  loading: boolean;
  refreshing: boolean;
  /** Lista completa de Pix da ordem (deduplicada e ordenada para abas). */
  payloads: PixPayload[];
  /** Pix da aba ativa. */
  payload: PixPayload | null;
  /** Índice da aba ativa. */
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  /** Agregados para o cabeçalho consolidado (total, pagos, quantidade). */
  aggregates: PixAggregates;
  /** Status de pagamento do Pix da aba ativa. */
  paymentStatus: PixModalPaymentStatus;
  error: string | null;
  refreshQrCode: () => Promise<void>;
}

export function usePixPaymentModal({
  salesOrderNumber,
  bp,
  enabled = true,
  pollingInterval = 60000,
}: UsePixPaymentModalOptions): UsePixPaymentModalReturn {
  const iocContext = useIoCContext();
  const { enqueueSnackbar } = useSnackbar();

  const getPixByOrderServiceRef = useRef<IGetPixByOrderService>(
    iocContext.serviceContainer.get<IGetPixByOrderService>(
      Types.Orders.IGetPixByOrderService
    )
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [payloads, setPayloads] = useState<PixPayload[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  // Flag para evitar que o polling sobrescreva o resultado do refresh
  const isRefreshingRef = useRef(false);
  // Mantém o índice ativo acessível dentro do refresh sem recriar o callback.
  const activeIndexRef = useRef(0);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const fetchPix = useCallback(
    async (isRefresh = false) => {
      if (!salesOrderNumber) return;
      // Não executa se um refresh manual está em andamento
      if (isRefreshingRef.current) return;

      try {
        if (!isRefresh) {
          // Só mostra loading na carga inicial — o polling atualiza silenciosamente
          setLoading(true);
        }
        const result = await getPixByOrderServiceRef.current.execute(
          salesOrderNumber
        );
        // Deduplica (D2) e ordena para abas (D3): principal primeiro.
        const prepared = preparePixPayloadsForModal(result);
        setPayloads(prepared);
        // Preserva a aba selecionada pelo usuário; corrige índice fora de faixa.
        setActiveIndex((prev) =>
          prepared.length === 0 ? 0 : Math.min(prev, prepared.length - 1)
        );
        setError(null);
      } catch (err: any) {
        // Polling silencioso: não exibe erro para não perturbar o usuário
        if (!isRefresh) {
          setError(err.message ?? "Erro ao carregar dados do Pix");
        }
      } finally {
        setLoading(false);
      }
    },
    [salesOrderNumber]
  );

  const payload = payloads[activeIndex] ?? payloads[0] ?? null;

  const aggregates = useMemo(() => getPixAggregates(payloads), [payloads]);

  // Existe ao menos um Pix pendente? (regra confiável via isPixPaid) — D8
  const hasPendingPix = useMemo(
    () => payloads.length === 0 || payloads.some((p) => !isPixPaid(p)),
    [payloads]
  );

  const paymentStatus: PixModalPaymentStatus =
    payload && isPixPaid(payload) ? "confirmed" : "pending";

  const openModal = useCallback(() => {
    setOpen(true);
    setActiveIndex(0);
    if (enabled && salesOrderNumber) {
      fetchPix(false);
    }
  }, [enabled, salesOrderNumber, fetchPix]);

  const closeModal = useCallback(() => {
    setOpen(false);
    stopPolling();
  }, [stopPolling]);

  const refreshQrCode = useCallback(async () => {
    if (!salesOrderNumber || !bp) return;

    // O intervalo de polling segue ativo, mas isRefreshingRef bloqueia o
    // fetchPix durante o refresh, evitando que o QR Code novo seja
    // sobrescrito pela resposta em cache do GET /pix/qrcode. Ao concluir,
    // o polling volta a executar naturalmente no próximo tick.
    isRefreshingRef.current = true;

    try {
      setRefreshing(true);
      // Captura o índice ativo ANTES do await: se o usuário trocar de aba
      // durante a requisição, o QR Code novo é gravado na aba que estava ativa
      // no momento do clique, evitando a corrida de aba.
      const targetIndex = activeIndexRef.current;
      const emv = await getPixByOrderServiceRef.current.refresh(
        salesOrderNumber,
        bp
      );
      // Atualiza somente o Pix da aba ativa com o novo QR Code.
      setPayloads((prev) =>
        prev.map((p, idx) =>
          idx === targetIndex ? { ...p, copiaECola: emv } : p
        )
      );
      setError(null);
    } catch (err: any) {
      enqueueSnackbar(
        err?.message ??
          "Não foi possível atualizar o QR Code. Tente novamente.",
        { variant: "error" }
      );
    } finally {
      setRefreshing(false);
      isRefreshingRef.current = false;
    }
  }, [salesOrderNumber, bp, enqueueSnackbar]);

  // Polling enquanto modal está aberto e houver ao menos um Pix pendente
  useEffect(() => {
    if (open && hasPendingPix && salesOrderNumber) {
      pollingRef.current = setInterval(() => {
        fetchPix(true);
      }, pollingInterval);
    }

    return () => {
      stopPolling();
    };
  }, [
    open,
    hasPendingPix,
    salesOrderNumber,
    pollingInterval,
    fetchPix,
    stopPolling,
  ]);

  return {
    open,
    openModal,
    closeModal,
    loading,
    refreshing,
    payloads,
    payload,
    activeIndex,
    setActiveIndex,
    aggregates,
    paymentStatus,
    error,
    refreshQrCode,
  };
}
